import React, { useEffect, useState, useRef } from 'react';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider
} from "../components/ui/tooltip";
import { Button } from '../components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveMenu } from '../store/auth/actions';
import { ChevronDoubleDownIcon } from '@heroicons/react/24/outline';
import { resetUploadForm } from '../store/uploads/actions';
import { resetExtractionForm } from '../store/extractions/action';

const MenuContent = ({ name, type, Icon, activeMenu, isOpen, level = 1 }) => {
    const isActive = activeMenu[level] === name;
    const iconClasses = `flex-shrink-0 h-[1.15rem] w-[1.15rem]`;
    const chevronClasses = `h-[15px] w-[15px] ${isOpen ? "rotate-180" : ""}`;

    return (
        <div className={`w-full flex justify-between items-center ${level === 2 ? "pl-4" : ""}`}>
            <div className="flex items-center">
                {Icon && <Icon className={iconClasses} aria-hidden="true" />}
                <span className={`flex ms-1 ${type === "submenu" && isActive ? 'font-bold text-primary' : ''}`}>
                    {name}
                </span>
            </div>
            {type === "menu-group" && <ChevronDoubleDownIcon className={chevronClasses} />}
        </div>
    );
};

const MenuItem = ({ name, Icon, path, type, showIconsOnly, children, parent = [] }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasManuallyToggled = useRef(false); // ← tracks user-initiated closes
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { activeMenu, user } = useSelector((state) => state.Auth);

    const handleMenuItemClick = () => {
        if (name === "Upload New") {
            dispatch(resetUploadForm(user));
        }
        if (name === "Extract New") {
            dispatch(resetExtractionForm());
        }
        const updatedParents = [...parent, name];
        if (type === "submenu" || type === "sub-submenu") {
            navigate(path);
            dispatch(setActiveMenu(updatedParents));
        } else if (type === "menu-group") {
            hasManuallyToggled.current = true; // ← user is explicitly toggling
            setIsOpen(prevState => !prevState);
        } else {
            dispatch(setActiveMenu([name]));
            navigate(path);
        }
    };

    useEffect(() => {
        const targetMenu = [...parent, name];
        const isSameActiveMenu =
            activeMenu.length === targetMenu.length &&
            activeMenu.every((item, idx) => item === targetMenu[idx]);

        if (path === location.pathname) {
            if (!isSameActiveMenu) {
                dispatch(setActiveMenu(targetMenu));
            }
            if (!isOpen) {
                setIsOpen(true);
            }
        } else if (type === "menu-group" && children?.some(child => child.to === location.pathname)) {
            if (!hasManuallyToggled.current) {
                setIsOpen(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const commonProps = {
        onClick: handleMenuItemClick,
        variant: name === activeMenu[0] ? "menu" : type === "menu-group" ? "menu_accent" : "ghost",
        className: `w-full flex justify-start ps-3 pe-4 ${type === "submenu" && 'hover:bg-transparent'}`
    };

    return (
        <>
            <div className='mb-1 block'>
                {showIconsOnly ? (
                    <TooltipProvider delayDuration={0} key={name}>
                        <Tooltip>
                            <TooltipTrigger>
                                <div
                                    className={`inline-flex items-center justify-center text-center text-gray-900 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 cursor-pointer px-3 py-2 hover:bg-accent hover:text-accent-foreground ${activeMenu[0] === name ? 'bg-primary shadow hover:bg-primary text-primary-foreground hover:text-default' : 'hover:bg-gray-100'}`}
                                    onClick={handleMenuItemClick}
                                >
                                    {Icon ? <Icon className={`${activeMenu[0] === name && 'text-primary-foreground hover:text-default'} h-5 w-5 font-bold`} /> : <p className={`${type === "submenu" && activeMenu[1] === name && 'font-bold text-primary'}`}>•</p>}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="flex bg-primary items-center gap-4 mb-10">
                                <span className="ml-auto">{name}</span>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ) : (
                    <Button {...commonProps} key={name}>
                        <MenuContent
                            name={name}
                            type={type}
                            Icon={Icon}
                            activeMenu={activeMenu}
                            isOpen={isOpen}
                        />
                    </Button>
                )}
                {type === "menu-group" && isOpen && (
                    <div className={`${!showIconsOnly && 'pl-5'}`}>
                        {children.map((child, idx) => (
                            <MenuItem
                                key={idx}
                                name={child.name}
                                Icon={child.Icon}
                                path={child.to}
                                type={child.children ? "menu-group" : "submenu"}
                                showIconsOnly={showIconsOnly}
                                parent={[...parent, name]}
                                children={child.children || []}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

export default MenuItem;